import React, { useState } from "react";
import { Card, InputNumber, Button, Table, message, Typography } from "antd";
import { DownloadOutlined, FileSearchOutlined } from "@ant-design/icons";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import api from "../api";

const { Title } = Typography;

const ReportesSemanalPage = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    // Calculate current week number
    const getWeekNumber = (d) => {
        const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
        const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
        const weekNo = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
        return weekNo;
    };

    const currentWeek = getWeekNumber(currentDate);

    const [year, setYear] = useState(currentYear);
    const [week, setWeek] = useState(currentWeek);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [allCalibres, setAllCalibres] = useState([]);

    const fetchData = async () => {
        if (!year || !week) {
            message.warning("Por favor ingrese año y semana");
            return;
        }

        if (week < 1 || week > 53) {
            message.warning("La semana debe estar entre 1 y 53");
            return;
        }

        setLoading(true);
        try {
            const res = await api.get(`/proceso/report/weekly?year=${year}&week=${week}`);
            const fetchedData = res.data;

            // Collect all unique calibres
            const calibresSet = new Set();
            fetchedData.forEach(item => {
                if (item.calibres && typeof item.calibres === 'object') {
                    Object.keys(item.calibres).forEach(calibre => calibresSet.add(calibre));
                }
            });

            const uniqueCalibres = Array.from(calibresSet).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
            setAllCalibres(uniqueCalibres);
            setData(fetchedData);
            message.success(`Se encontraron ${fetchedData.length} registros`);
        } catch (err) {
            console.error(err);
            message.error("Error al cargar los datos del reporte");
            setData([]);
            setAllCalibres([]);
        } finally {
            setLoading(false);
        }
    };

    const exportToExcel = async () => {
        if (!data || data.length === 0) {
            message.warning("No hay datos para exportar. Por favor genere el reporte primero.");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet(`Semana ${week} - ${year}`);

        // --- Headers ---
        const baseHeadersBeforeCalibres = ["Año", "Semana", "Exportador", "Productor", "Producto", "Peso de Caja", "Fecha", "Secuencia", "Número de Gavetas", "Peso de Ingreso (kg)", "Total de Cajas"];
        const baseHeadersAfterCalibres = ["Peso Procesado (kg)", "Rechazo (kg)", "Porcentaje de Rechazo"];
        const calibreHeaders = allCalibres.map(c => `Calibre: ${c}`);
        const allHeaders = [...baseHeadersBeforeCalibres, ...calibreHeaders, ...baseHeadersAfterCalibres];

        const headerRow = sheet.addRow(allHeaders);
        headerRow.height = 28;
        headerRow.eachCell(cell => {
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FF217346" } // Excel green
            };
            cell.font = { color: { argb: "FFFFFFFF" }, bold: true, size: 11 };
            cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" }
            };
        });

        // Freeze header row
        sheet.views = [{ state: "frozen", ySplit: 1 }];

        // --- Data rows ---
        data.forEach((item, index) => {
            const sec = item.secuencial || item.secuencia || "";
            const gav = item.gavetas ?? item.totalProcesadas ?? item.totalProcesadasGranel ?? 0;
            const pesoIng = item.pesoIngresado ?? item.pesoIngreso ?? 0;
            const pctRechazo = item.porcentajeRechazo !== undefined
                ? item.porcentajeRechazo
                : (pesoIng ? ((item["rechazo en kg"] || 0) / pesoIng * 100) : 0);

            const rowData = [
                item.Year || "",
                item["Week Number"] || "",
                item.exportadorNombre || "",
                item.productorName || "",
                item.producto || "",
                item["kg por caja"] || 0,
                item.fecha || "",
                sec,
                gav,
                parseFloat(parseFloat(pesoIng).toFixed(2)),
                item.cajas || 0,
                ...allCalibres.map(c =>
                    item.calibres && item.calibres[c] !== undefined ? item.calibres[c] : 0
                ),
                item.pesoProcesado || 0,
                item["rechazo en kg"] || 0,
                parseFloat((pctRechazo / 100).toFixed(4))
            ];

            const row = sheet.addRow(rowData);
            row.eachCell(cell => {
                cell.alignment = { vertical: "middle", horizontal: "center" };
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" }
                };
                // Zebra striping
                if (index % 2 === 0) {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFF0F7EE" } // Very light green tint
                    };
                }
            });
            // Format numeric columns to decimal number style
            row.getCell(10).numFmt = "#,##0.00";
            row.getCell(12 + allCalibres.length).numFmt = "#,##0.00";
            row.getCell(13 + allCalibres.length).numFmt = "#,##0.00";
            // Format "Porcentaje de Rechazo" column to percentage style
            row.getCell(14 + allCalibres.length).numFmt = "0.00%";
        });

        // --- Totals row ---
        const totalGavetas = data.reduce(
            (s, r) => s + Number(r.gavetas ?? r.totalProcesadas ?? r.totalProcesadasGranel ?? 0),
            0
        );
        const totalPesoIngreso = data.reduce((s, r) => s + (r.pesoIngresado ?? r.pesoIngreso ?? 0), 0);
        const totalCajas = data.reduce((s, r) => s + (r.cajas || 0), 0);
        const totalRechazo = data.reduce((s, r) => s + (r["rechazo en kg"] || 0), 0);
        const totalPesoProcesado = data.reduce((s, r) => s + (r.pesoProcesado || 0), 0);
        const totalCalibresCols = allCalibres.map(c =>
            data.reduce((s, r) => s + (r.calibres && r.calibres[c] ? r.calibres[c] : 0), 0)
        );
        const totalPorcentajeRechazo = totalPesoIngreso > 0 ? (totalRechazo / totalPesoIngreso * 100) : 0;

        const totalsRowData = [
            "TOTAL",
            "",
            "",
            "",
            "",
            "", // Peso de Caja
            "",
            "",
            totalGavetas,
            parseFloat(totalPesoIngreso.toFixed(2)),
            totalCajas,
            ...totalCalibresCols,
            parseFloat(totalPesoProcesado.toFixed(2)),
            parseFloat(totalRechazo.toFixed(2)),
            parseFloat((totalPorcentajeRechazo / 100).toFixed(4))
        ];

        const totalsRow = sheet.addRow(totalsRowData);
        totalsRow.eachCell(cell => {
            cell.font = { bold: true, size: 11 };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFE9F5EE" } // Light green
            };
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "double" },
                right: { style: "thin" }
            };
            cell.alignment = { vertical: "middle", horizontal: "center" };
        });
        // Format totals numeric columns to decimal number style
        totalsRow.getCell(10).numFmt = "#,##0.00";
        totalsRow.getCell(12 + allCalibres.length).numFmt = "#,##0.00";
        totalsRow.getCell(13 + allCalibres.length).numFmt = "#,##0.00";
        // Format totals "Porcentaje de Rechazo" column to percentage style
        totalsRow.getCell(14 + allCalibres.length).numFmt = "0.00%";

        // --- Column widths ---
        const colWidthsBeforeCalibres = [8, 10, 28, 22, 14, 14, 12, 16, 18, 22, 14];
        const colWidthsAfterCalibres = [18, 14, 16];

        colWidthsBeforeCalibres.forEach((w, i) => {
            sheet.getColumn(i + 1).width = w;
        });
        allCalibres.forEach((_, i) => {
            sheet.getColumn(colWidthsBeforeCalibres.length + 1 + i).width = 14;
        });
        colWidthsAfterCalibres.forEach((w, i) => {
            sheet.getColumn(colWidthsBeforeCalibres.length + allCalibres.length + 1 + i).width = w;
        });

        // --- Generate and download ---
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
        });
        saveAs(blob, `Reporte_Semanal_${year}_Semana${week}.xlsx`);
        message.success("Excel exportado exitosamente");
    };

    // Create table columns
    const baseColumns = [
        {
            title: "Año",
            dataIndex: "Year",
            key: "Year",
            width: 80
        },
        {
            title: "Semana",
            dataIndex: "Week Number",
            key: "Week Number",
            width: 90
        },
        {
            title: "Exportador",
            dataIndex: "exportadorNombre",
            key: "exportadorNombre",
            width: 150
        },
        {
            title: "Productor",
            dataIndex: "productorName",
            key: "productorName",
            width: 150
        },
        {
            title: "Producto",
            dataIndex: "producto",
            key: "producto",
            width: 120
        },
        {
            title: "Peso de Caja",
            dataIndex: "kg por caja",
            key: "kg por caja",
            width: 100
        },
        {
            title: "Fecha",
            dataIndex: "fecha",
            key: "fecha",
            width: 110
        },
        {
            title: "Secuencia",
            dataIndex: "secuencial",
            key: "secuencial",
            width: 100,
            render: (val, record) => record.secuencial || record.secuencia || "-"
        },
        {
            title: "Número de Gavetas",
            dataIndex: "gavetas",
            key: "gavetas",
            width: 130,
            render: (val, record) => {
                const gav = record.gavetas ?? record.totalProcesadas ?? record.totalProcesadasGranel;
                return gav !== undefined ? gav : "-";
            }
        },
        {
            title: "Peso de Ingreso (kg)",
            dataIndex: "pesoIngresado",
            key: "pesoIngresado",
            width: 140,
            render: (val, record) => {
                const peso = record.pesoIngresado ?? record.pesoIngreso;
                return peso !== undefined && peso !== null ? parseFloat(peso).toFixed(2) : "-";
            }
        },
        {
            title: "Cajas",
            dataIndex: "cajas",
            key: "cajas",
            width: 80
        },
        {
            title: "Peso Procesado (kg)",
            dataIndex: "pesoProcesado",
            key: "pesoProcesado",
            width: 140,
            render: (val) => {
                return val !== undefined && val !== null ? parseFloat(val).toFixed(2) : "-";
            }
        },
        {
            title: "Rechazo (kg)",
            dataIndex: "rechazo en kg",
            key: "rechazo en kg",
            width: 120,
            render: (val) => {
                return val !== undefined && val !== null ? parseFloat(val).toFixed(2) : "-";
            }
        },
        {
            title: "% Rechazo",
            dataIndex: "porcentajeRechazo",
            key: "porcentajeRechazo",
            width: 110,
            render: (val, record) => {
                const pct = record.porcentajeRechazo ?? (record.pesoIngresado ? ((record["rechazo en kg"] || 0) / record.pesoIngresado * 100) : 0);
                return pct != null ? `${parseFloat(pct).toFixed(2)}%` : "-";
            }
        }
    ];

    const calibreColumns = allCalibres.map(calibre => ({
        title: `Calibre: ${calibre}`,
        key: `calibre_${calibre}`,
        width: 100,
        render: (_, record) => {
            return record.calibres && record.calibres[calibre] !== undefined
                ? record.calibres[calibre]
                : "-";
        }
    }));

    const columns = [
        ...baseColumns.slice(0, 11),
        ...calibreColumns,
        ...baseColumns.slice(11)
    ];

    return (
        <div>
            <Title level={3}>Reporte Semanal de Procesos</Title>

            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
                    <div>
                        <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>
                            Año:
                        </label>
                        <InputNumber
                            min={2000}
                            max={2100}
                            value={year}
                            onChange={setYear}
                            style={{ width: 120 }}
                        />
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>
                            Semana:
                        </label>
                        <InputNumber
                            min={1}
                            max={53}
                            value={week}
                            onChange={setWeek}
                            style={{ width: 120 }}
                        />
                    </div>

                    <Button
                        type="primary"
                        icon={<FileSearchOutlined />}
                        onClick={fetchData}
                        loading={loading}
                        style={{ marginTop: "24px" }}
                    >
                        Generar Reporte
                    </Button>

                    <Button
                        type="default"
                        icon={<DownloadOutlined />}
                        onClick={exportToExcel}
                        disabled={data.length === 0}
                        style={{ marginTop: "24px" }}
                    >
                        Exportar Excel
                    </Button>
                </div>
            </Card>

            {data.length > 0 && (
                <Card title={`Resultados: ${data.length} registros`}>
                    <Table
                        dataSource={data}
                        columns={columns}
                        rowKey={(record, index) => index}
                        pagination={{ pageSize: 20 }}
                        scroll={{ x: 'max-content' }}
                        size="small"
                    />
                </Card>
            )}

            {!loading && data.length === 0 && (
                <Card>
                    <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                        Seleccione año y semana, luego haga clic en "Generar Reporte" para ver los datos.
                    </div>
                </Card>
            )}
        </div>
    );
};

export default ReportesSemanalPage;
